export interface NavLookupItem {
  id: string;
  path?: string;
  enabled?: boolean;
}

/**
 * Returns the id of the nav item whose path matches the current pathname,
 * or `null` if no enabled item matches. An empty pathname is treated as `/`.
 * Disabled items (enabled === false) never match.
 *
 * Matching is exact, plus a prefix fallback for nested routes: `/settings/mcp`
 * resolves to the `/settings` item so a sub-route keeps its parent's sidebar
 * entry highlighted. The root path `/` is exempt from the prefix rule so it
 * doesn't swallow every route.
 *
 * An exact match always wins over a prefix match regardless of item order, so a
 * caller that lists a parent route before a more-specific child still resolves
 * `/settings/advanced` to the `/settings/advanced` item, not `/settings`.
 */
export function getActiveNavId(
  pathname: string,
  items: ReadonlyArray<NavLookupItem>
): string | null {
  const normalized = pathname === '' ? '/' : pathname;
  const candidates = items.filter((item) => item.enabled !== false && item.path);
  // Exact match always wins — checked across all candidates before any prefix
  // fallback, so item order can't let a parent route shadow an exact child.
  const exact = candidates.find((item) => item.path === normalized);
  if (exact) return exact.id;
  // Prefix fallback for nested routes (root `/` exempted so it isn't greedy).
  const prefix = candidates.find(
    (item) => item.path !== '/' && normalized.startsWith(`${item.path}/`)
  );
  return prefix?.id ?? null;
}
